import type {
  GoalsModel,
  QuizAnswersStorageModel,
  QuizCollectionModel,
  QuizConfigModel,
} from '../models/quiz.model';
import { resolveQuizPoints } from '../utils/progressPoints';

export function useQuiz() {
  const {
    $quiz,
    $logger,
  } = useNuxtApp();
  const route = useRoute();
  const store = useKegelAnalyticStore();
  const goals = ref<GoalsModel>({
    main: store.goals?.main ?? '',
    additional: store.goals?.additional ?? '',
  });

  function setGoals(newGoals: GoalsModel): void {
    goals.value = newGoals;

    store.goals = newGoals;
  }

  function setAddGoal(choice: boolean): void {
    store.addGoal = choice;
  }

  function getQuizAnswers(): QuizAnswersStorageModel {
    return (store.quiz || {}) as QuizAnswersStorageModel;
  }

  function getQuizSteps(): number {
    return store.steps;
  }

  function saveAnswerToLocally(answerId: number) {
    const localQuiz = (store.quiz || {}) as QuizAnswersStorageModel;
    const savedQuiz = Number(currentQuestionId.value) === 0 ? {} : { ...localQuiz };

    savedQuiz[currentQuestionId.value] = answerId;
    // Записываем ответы в объект, где ключ объекта id вопроса, а value объекта это id ответа
    store.quiz = savedQuiz;
  }

  const currentQuestionId = computed((): string => {
    return route.params.id as string;
  });

  const currentQuestion = computed((): QuizConfigModel => {
    const question = quiz.value?.[route.params.id as string] as QuizConfigModel | undefined;

    if (!question) {
      $logger.error(new Error('Quiz currentQuestion not found'), {
        tags: {
          flow: 'quiz',
          step: 'current_question',
        },
        extra: {
          currentQuestionId: route.params.id,
          availableKeys: quiz.value ? Object.keys(quiz.value) : null,
        },
      });
    }

    return question as QuizConfigModel;
  });

  const nextLink = computed((): string => {
    const link = currentQuestion.value?.nextLink;

    if (Number.isInteger(link)) {
      const target = quiz.value?.[link as number];
      if (!target) {
        $logger.error(new Error('Quiz nextLink target question not found'), {
          tags: {
            flow: 'quiz',
            step: 'next_link',
          },
          extra: {
            currentQuestionId: currentQuestionId.value,
            nextLink: link,
          },
        });
      }
      return `/quiz/${link}/${target.type}`;
    }

    return link as string;
  });

  const prevLink = computed((): string => {
    const link = currentQuestion.value.prevLink;

    if (Number.isInteger(link)) {
      const target = quiz.value?.[link as number];

      if (!target) {
        $logger.error(new Error('Quiz prevLink target question not found'), {
          tags: {
            flow: 'quiz',
            step: 'prev_link',
          },
          extra: {
            currentQuestionId: currentQuestionId.value,
            prevLink: link,
          },
        });
      }
      return `/quiz/${link}/${target.type}`;
    }

    return `${link}`;
  });

  const currentProgress = computed((): number => {
    if (nextLink.value === '/quiz/creating-plan') {
      return 100;
    }
    return currentQuestion.value?.progress ?? 0;
  });

  const progressPoints = computed(() => resolveQuizPoints(getQuizSteps()));

  const quiz = computed((): QuizCollectionModel => {
    return $quiz?.value.quiz as QuizCollectionModel;
  });

  return {
    currentQuestionId,
    currentQuestion,
    nextLink,
    prevLink,
    currentProgress,
    progressPoints,
    setGoals,
    goals,
    setAddGoal,
    saveAnswerToLocally,
    getQuizAnswers,
    getQuizSteps,
    quiz,
  };
}
